import {
  ArrowRight,
  BellRing,
  Building2,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Flag,
  GraduationCap,
  Handshake,
  HeartHandshake,
  MapPinned,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { Button } from "@/components/ui/button";
import { homepageStats } from "@/lib/siteContent";

const comparisonRows = [
  {
    legacy: "Limited visibility once the journey begins",
    carefleet: "Live journey tracking and milestone visibility for key stakeholders",
  },
  {
    legacy: "Manual updates and reactive communication",
    carefleet: "Structured workflows with clearer updates and operational accountability",
  },
  {
    legacy: "No consistent handover records",
    carefleet: "Controlled handover confirmation supported by clear checkpoints",
  },
  {
    legacy: "Incidents handled with fragmented records",
    carefleet: "Incident logging with traceable workflow and audit-ready records",
  },
];

const audiences = [
  {
    title: "Local authorities",
    icon: Building2,
    summary: "Procurement-ready transport built around safeguarding, operational clarity, and visible risk reduction.",
    link: "/procurement",
  },
  {
    title: "Schools",
    icon: GraduationCap,
    summary: "Reliable pupil coordination with visibility into arrivals, issues, and communication pathways.",
    link: "/schools",
  },
  {
    title: "Families",
    icon: HeartHandshake,
    summary: "Reassurance through live updates, secure handovers, and shareable journey visibility.",
    link: "/families",
  },
];

const journeySteps = [
  {
    title: "Journey scheduled",
    detail: "A journey is scheduled and assigned to a suitable wheelchair accessible vehicle (WAV).",
    icon: CalendarClock,
  },
  {
    title: "Vehicle readiness",
    detail: "The driver completes pre-journey checks and readiness confirmation before departure.",
    icon: ClipboardCheck,
  },
  {
    title: "Stakeholder notified",
    detail: "Parents or stakeholders are notified ahead of arrival to support calm, prepared handovers.",
    icon: BellRing,
  },
  {
    title: "Secure pickup",
    detail: "A secure handover takes place at pickup with confirmation and controlled transition.",
    icon: Handshake,
  },
  {
    title: "Live oversight",
    detail: "Live tracking and milestone updates provide visibility throughout the journey.",
    icon: MapPinned,
  },
  {
    title: "Arrival confirmed",
    detail: "Arrival is confirmed with a secure handover to the receiving setting or responsible adult.",
    icon: Flag,
  },
  {
    title: "Audit trail logged",
    detail: "The journey is logged with a full audit trail to support accountability and review.",
    icon: FileText,
  },
];

const alignmentPoints = [
  "Secure handover approach",
  "Communication pathways",
  "Incident awareness and logging",
  "Role-based visibility",
];

export const HomePage = () => {
  return (
    <div className="overflow-hidden">
      <section className="grid-overlay relative px-4 pb-20 pt-16 sm:px-6 lg:px-8 lg:pb-24 lg:pt-24">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20">
          <Reveal>
            <div className="space-y-8">
              <div className="space-y-5">
                <p className="eyebrow" data-testid="home-hero-eyebrow">
                  Specialist safeguarding transport for the UK
                </p>
                <h1
                  className="font-heading text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl"
                  data-testid="home-hero-title"
                >
                  Specialist transport with real-time visibility and safeguarding built in.
                </h1>
                <p
                  className="max-w-2xl text-base leading-8 text-slate-600 sm:text-lg"
                  data-testid="home-hero-description"
                >
                  CareFleet combines professional transport with a digital platform that gives parents,
                  schools, and councils full visibility of every journey. Human care stays at the centre,
                  while digital control brings structure, visibility, and accountability. CareFleet has
                  been designed from the ground up to deliver structured, safeguarding-focused transport
                  with real-time visibility.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                {homepageStats.map((stat) => (
                  <div
                    key={stat.label}
                    className="metric-chip"
                    data-testid={`home-stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span className="font-semibold text-brand-navy">{stat.value}</span>
                    <span>{stat.label}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button asChild className="rounded-full bg-brand-navy px-6 py-6 text-white hover:bg-brand-navy/90">
                  <Link to="/contact" data-testid="home-request-demo-button">
                    Request Demo
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full border-brand-navy/20 px-6 py-6 text-brand-navy hover:bg-brand-mist">
                  <Link to="/procurement" data-testid="home-discuss-procurement-button">
                    Discuss Procurement Requirements
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="panel-card max-w-2xl p-6">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-brand-teal">Core positioning</p>
                <p className="mt-3 font-heading text-2xl font-semibold text-slate-950" data-testid="home-core-positioning-text">
                  Transport is just the vehicle. CareFleet is the system behind it.
                </p>
                <p className="mt-2 text-slate-600" data-testid="home-core-supporting-text">
                  Designed to reduce uncertainty through structured workflows and real-time oversight.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative">
              <div className="image-shell hero-glow overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1732194439331-08ec61c4df4f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                  alt="Wheelchair accessible vehicle with ramp supporting a calm passenger handover"
                  className="aspect-[5/6] w-full object-cover object-center"
                  data-testid="home-hero-image"
                />
              </div>

              <div className="panel-card absolute -bottom-10 left-4 right-4 p-6 sm:left-10 sm:right-auto sm:w-[27rem]">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-teal">Illustrative operational view</p>
                <p className="mt-3 font-heading text-2xl font-semibold text-slate-950" data-testid="home-platform-panel-title">
                  Clear checkpoints across the journey
                </p>
                <div className="mt-5 space-y-3">
                  {[
                    "Pre-journey checks completed",
                    "Live milestone updates in motion",
                    "Controlled handover confirmation",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3" data-testid={`home-illustrative-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="h-5 w-5 text-brand-teal" />
                      <p className="text-sm text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <SectionHeading
            eyebrow="Why CareFleet is different"
            title="CareFleet is designed from the ground up for visibility, safeguarding, and operational discipline."
            description="Competitors often focus on friendliness and transport availability. CareFleet is designed around structured workflows, digital accountability, and calm operational clarity."
            testId="home-comparison-heading"
          />

          <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
            <Reveal>
              <div className="soft-card h-full p-8" data-testid="home-comparison-summary-card">
                <div className="inline-flex items-center gap-2 rounded-full bg-brand-mist px-4 py-2 text-sm font-semibold text-brand-navy">
                  <ShieldCheck className="h-4 w-4" />
                  Accountability-first model
                </div>
                <h3 className="mt-6 font-heading text-3xl font-semibold text-slate-950">
                  Minimising risk through visibility, accountability, and controlled handovers.
                </h3>
                <p className="mt-4 text-base leading-8 text-slate-600">
                  From secure handovers to role-based oversight, CareFleet gives stakeholders a clearer line of sight into journey status, safeguarding steps, and operational checkpoints.
                </p>
                <div className="mt-8 space-y-4">
                  {[
                    "Designed to reduce uncertainty through structured workflows and real-time oversight",
                    "Minimising risk through visibility, accountability, and controlled handovers",
                    "Clear operational checkpoints support safeguarding and consistency",
                  ].map((signal) => (
                    <div key={signal} className="flex gap-3" data-testid={`home-signal-${signal.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-5 w-5 text-brand-teal" />
                      <p className="text-slate-700">{signal}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="soft-card overflow-hidden" data-testid="home-comparison-table-card">
                <div className="grid grid-cols-[0.9fr_1.1fr] border-b border-slate-200 bg-slate-50/80 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                  <div className="p-5">Traditional transport</div>
                  <div className="bg-brand-navy p-5 text-white">CareFleet</div>
                </div>
                {comparisonRows.map((row) => (
                  <div key={row.legacy} className="grid grid-cols-1 border-b border-slate-100 last:border-b-0 md:grid-cols-[0.9fr_1.1fr]">
                    <div className="p-6 text-base leading-7 text-slate-600" data-testid={`home-legacy-${row.legacy.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {row.legacy}
                    </div>
                    <div className="bg-brand-mist/50 p-6 text-base font-medium leading-7 text-slate-800" data-testid={`home-carefleet-${row.carefleet.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {row.carefleet}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 px-4 py-24 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <SectionHeading
            eyebrow="How a journey works"
            title="How a CareFleet journey works"
            description="Clear operational checkpoints support safeguarding and consistency across scheduling, handovers, visibility, and final logging."
            testId="home-journey-works-heading"
          />
          <div className="grid gap-6 lg:grid-cols-3">
            {journeySteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Reveal key={step.title}>
                  <div className="rounded-[28px] border border-white/10 bg-white/5 p-6" data-testid={`home-journey-step-${step.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-brand-teal">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-300">Step {index + 1}</p>
                        <p className="font-heading text-2xl font-semibold text-white">{step.title}</p>
                      </div>
                    </div>
                    <p className="mt-4 text-base leading-7 text-slate-300">{step.detail}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <SectionHeading
            eyebrow="Use cases"
            title="Purpose-built for local authorities, schools, and families."
            description="CareFleet adapts its service model to the needs of procurement teams, education settings, and households without losing operational consistency."
            testId="home-use-cases-heading"
          />

          <div className="grid gap-6 lg:grid-cols-3">
            {audiences.map((audience) => {
              const Icon = audience.icon;
              return (
                <Reveal key={audience.title}>
                  <div className="soft-card flex h-full flex-col p-8" data-testid={`home-audience-${audience.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-card`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-mist text-brand-navy">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mt-6 font-heading text-2xl font-semibold text-slate-950">{audience.title}</h3>
                    <p className="mt-4 flex-1 text-base leading-8 text-slate-600">{audience.summary}</p>
                    <Button asChild variant="ghost" className="mt-6 w-fit rounded-full px-0 text-brand-navy hover:bg-transparent hover:text-brand-teal">
                      <Link to={audience.link} data-testid={`home-audience-${audience.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-link`}>
                        Explore {audience.title}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 pb-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="soft-card grid gap-8 p-8 lg:grid-cols-[0.95fr_1.05fr] lg:p-10" data-testid="home-safeguarding-alignment-card">
              <div>
                <p className="eyebrow">Safeguarding alignment</p>
                <h2 className="mt-4 font-heading text-3xl font-semibold text-slate-950">
                  CareFleet has been designed in alignment with UK safeguarding expectations and duty of care principles.
                </h2>
                <p className="mt-4 text-base leading-8 text-slate-600">
                  The service model focuses on secure handovers, communication pathways, incident awareness, and role-based visibility without making unsupported certification claims.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {alignmentPoints.map((item) => (
                  <div key={item} className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid={`home-alignment-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="h-5 w-5 text-brand-teal" />
                      <p className="font-medium text-slate-800">{item}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto flex max-w-7xl flex-col gap-8 rounded-[36px] bg-brand-navy px-8 py-10 text-white shadow-[0_35px_110px_-55px_rgba(30,58,138,0.85)] lg:flex-row lg:items-center lg:justify-between lg:px-12">
            <div>
              <p className="eyebrow text-white/80" data-testid="home-cta-eyebrow">See CareFleet in context</p>
              <h2 className="mt-3 font-heading text-3xl font-semibold sm:text-4xl" data-testid="home-cta-title">
                See how CareFleet can support your transport operations.
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-8 text-blue-100" data-testid="home-cta-description">
                From procurement planning to family reassurance, CareFleet is designed to reduce uncertainty through structured workflows and real-time oversight.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button asChild className="rounded-full bg-white px-6 py-6 text-brand-navy hover:bg-slate-100">
                <Link to="/contact" data-testid="home-final-request-demo-button">
                  Request Demo
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-white/30 bg-transparent px-6 py-6 text-white hover:bg-white/10">
                <Link to="/contact" data-testid="home-final-speak-to-team-button">
                  Speak to Our Team
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};